// Simplified Map Control Factory for Atlas Viewer
// SPDX-License-Identifier: MIT

import React from 'react';
import styled, { DefaultTheme } from 'styled-components';
import { MapControlFactory, withState } from '@kepler.gl/components';
import { SqlPanelControl } from '../integrations/sql-panel/components/SqlPanelControl';

// Styled components for map control overlay
interface StyledMapControlOverlayProps {
  top: number;
  rightPanelVisible: boolean;
  fullHeight?: boolean;
  theme: DefaultTheme;
}

const StyledMapControlPanel = styled.div`
  position: relative;
`;

const StyledMapControlOverlay = styled.div<StyledMapControlOverlayProps>`
  position: absolute;
  display: flex;
  top: ${props => props.top}px;
  right: 0;
  z-index: 1;
  pointer-events: none !important;
  & > * {
    pointer-events: all;
  }
  margin-top: ${props =>
    props.rightPanelVisible ? (props.theme as any).rightPanelMarginTop : 0}px;
  margin-right: ${props =>
    props.rightPanelVisible ? (props.theme as any).rightPanelMarginRight : 0}px;
  ${props => (props.fullHeight ? 'height' : 'max-height')}: calc(100% - ${props =>
    (props.theme as any).rightPanelMarginTop + (props.theme as any).bottomWidgetPaddingBottom}px);
  .map-control {
    ${props => (props.rightPanelVisible ? 'padding-top: 0px;' : '')}
  }
`;

// SQL Panel Control wrapper as an action component
const SqlPanelActionComponent: React.FC = () => <SqlPanelControl />;

// Simple map control factory for Atlas Viewer
const CustomMapControlFactory = (...deps: any[]) => {
  const MapControl = (MapControlFactory as any)(...deps);

  // Get default action components and add custom controls at the end
  const defaultComponents = MapControl.defaultActionComponents || [];
  const actionComponents = [...defaultComponents, SqlPanelActionComponent];

  // Custom map control component
  const CustomMapControl = (props: any) => (
    <StyledMapControlOverlay top={props.top || 0} rightPanelVisible={false}>
      <StyledMapControlPanel>
        <MapControl {...props} top={0} actionComponents={actionComponents} />
      </StyledMapControlPanel>
    </StyledMapControlOverlay>
  );

  // Use withState for Atlas Viewer state mapping - use 'map' to match the id in App.tsx
  return withState([], (state: any) => {
    const keplerGlState = state.keplerGl?.map;
    return keplerGlState || {};
  })(CustomMapControl);
};

// Set dependencies to match MapControlFactory
CustomMapControlFactory.deps = MapControlFactory.deps;

// Export replacement function for injectComponents
export const replaceMapControl = () => [MapControlFactory, CustomMapControlFactory];

export { MapControlFactory, CustomMapControlFactory };
export default CustomMapControlFactory;
